import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { rebateFormSchema, RebateFormValues } from "./schemas/rebateForm.schema";
import {
  useCreateSaccoRebate,
  useUpdateSaccoRebate,
} from "@saccoHooks";
import {SaccoRebate} from "@/@types/sacco.models.ts";

interface RebateFormProps {
  membershipId: string;
  nativeCurrency?: string;
  rebate?: SaccoRebate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function RebateForm({
  membershipId,
  nativeCurrency = 'KES',
  rebate,
  open,
  onOpenChange,
  onSuccess,
}: RebateFormProps) {
  const isEdit = !!rebate;
  const createRebate = useCreateSaccoRebate(membershipId);
  const updateRebate = useUpdateSaccoRebate(membershipId);

  const form = useForm<RebateFormValues>({
    resolver: zodResolver(rebateFormSchema),
    defaultValues: {
      year: new Date().getFullYear() as unknown as number,
      rebate_amount: "" as unknown as number,
      dividend_amount: "" as unknown as number,
      note: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        year: rebate?.year ?? new Date().getFullYear(),
        rebate_amount: rebate?.rebate_amount != null ? Number(rebate.rebate_amount) : ("" as unknown as number),
        dividend_amount: rebate?.dividend_amount != null ? Number(rebate.dividend_amount) : ("" as unknown as number),
        note: rebate?.note ?? "",
      });
    }
  }, [open, rebate, form]);

  const isPending = createRebate.isPending || updateRebate.isPending;

  async function onSubmit(values: RebateFormValues) {
    try {
      if (isEdit) {
        await updateRebate.mutateAsync({ id: rebate.id, ...values });
        toast.success("Rebate updated successfully");
      } else {
        await createRebate.mutateAsync({
          year: values.year,
          rebate_amount: values.rebate_amount,
          dividend_amount: values.dividend_amount,
          note: values.note || null,
        });
        toast.success("Rebate recorded successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      // Catch Supabase unique constraint violation for duplicate year
      if (err?.code === "23505" || err?.message?.includes("unique")) {
        toast.error(`A rebate for year ${values.year} already exists for this membership.`);
      } else {
        toast.error(isEdit ? "Failed to update rebate" : "Failed to record rebate");
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Rebate" : "Add Rebate"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="year"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Year *</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder={String(new Date().getFullYear())} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rebate_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{`Rebate Amount (${nativeCurrency}) *`}</FormLabel>
                  <FormControl>
                    <Input type="text" inputMode="decimal" placeholder="0.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dividend_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{`Dividend Amount (${nativeCurrency}) *`}</FormLabel>
                  <FormControl>
                    <Input type="text" inputMode="decimal" placeholder="0.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional note"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
